import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import { authAPI } from '../../services/api';
import { COLORS } from '../../utils/constants';
import toast from 'react-hot-toast';
import {
  KeyRound, Lock, Eye, EyeOff, ShieldCheck, Check,
  AlertCircle, ArrowLeft, CheckCircle2
} from 'lucide-react';

const C = COLORS;

const ChangePasswordPage = () => {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const isMinLength = newPassword.length >= 6;
  const isMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isReady = currentPassword.trim() && isMinLength && isMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!currentPassword) {
      setErrorMsg('Please enter your current password.');
      return;
    }
    if (!isMinLength) {
      setErrorMsg('New password must be at least 6 characters long.');
      return;
    }
    if (!isMatch) {
      setErrorMsg('New password and confirmation password do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      const msg = res.data?.message || 'Password changed successfully!';
      setSuccessMsg(msg);
      toast.success(msg);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const errText = err.response?.data?.error || err.message || 'Failed to change password. Please verify current password.';
      setErrorMsg(errText);
      toast.error(errText);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout title="Change Password">
      <div style={{ maxWidth: 640, margin: '0 auto', paddingTop: 10 }}>
        {/* Back Link */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'none',
            border: 'none',
            color: C.g500,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
            marginBottom: 16,
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = C.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = C.g500}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Main Card */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          border: `1px solid ${C.g200}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}>
          {/* Header Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6366F1 100%)',
            padding: '28px 28px 24px',
            color: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 46,
                height: 46,
                borderRadius: 14,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.25)',
              }}>
                <KeyRound size={22} color="#fff" />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: -0.3 }}>
                  Change Your Password
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
                  Update your account password to keep your administrator portal secure.
                </p>
              </div>
            </div>
          </div>

          <div style={{ padding: '28px' }}>
            {/* Success Message Banner */}
            {successMsg && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 16px',
                borderRadius: 12,
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                color: '#065F46',
                marginBottom: 20,
                fontSize: 13.5,
                fontWeight: 600,
              }}>
                <CheckCircle2 size={18} color="#059669" />
                <span>{successMsg}</span>
              </div>
            )}

            {/* Error Message Banner */}
            {errorMsg && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 16px',
                borderRadius: 12,
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#991B1B',
                marginBottom: 20,
                fontSize: 13.5,
                fontWeight: 600,
              }}>
                <AlertCircle size={18} color="#DC2626" />
                <span>{errorMsg}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Current Password Field */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
                  Current Password <span style={{ color: C.danger }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    placeholder="Enter current password"
                    style={{
                      width: '100%',
                      padding: '11px 44px 11px 38px',
                      borderRadius: 10,
                      border: `1.5px solid ${C.g200}`,
                      fontSize: 14,
                      color: C.g900,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = C.primary}
                    onBlur={(e) => e.target.style.borderColor = C.g200}
                  />
                  <Lock size={16} color={C.g400} style={{ position: 'absolute', left: 12, top: 14 }} />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: 12,
                      background: 'none',
                      border: 'none',
                      padding: 2,
                      cursor: 'pointer',
                      color: C.g400,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {showCurrent ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* New Password Field */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
                  New Password <span style={{ color: C.danger }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Enter new password (min. 6 characters)"
                    style={{
                      width: '100%',
                      padding: '11px 44px 11px 38px',
                      borderRadius: 10,
                      border: `1.5px solid ${C.g200}`,
                      fontSize: 14,
                      color: C.g900,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = C.primary}
                    onBlur={(e) => e.target.style.borderColor = C.g200}
                  />
                  <Lock size={16} color={C.g400} style={{ position: 'absolute', left: 12, top: 14 }} />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: 12,
                      background: 'none',
                      border: 'none',
                      padding: 2,
                      cursor: 'pointer',
                      color: C.g400,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {showNew ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* Confirm New Password Field */}
              <div style={{ marginBottom: 22 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 6 }}>
                  Confirm New Password <span style={{ color: C.danger }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm your new password"
                    style={{
                      width: '100%',
                      padding: '11px 44px 11px 38px',
                      borderRadius: 10,
                      border: `1.5px solid ${C.g200}`,
                      fontSize: 14,
                      color: C.g900,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = C.primary}
                    onBlur={(e) => e.target.style.borderColor = C.g200}
                  />
                  <Lock size={16} color={C.g400} style={{ position: 'absolute', left: 12, top: 14 }} />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: 12,
                      background: 'none',
                      border: 'none',
                      padding: 2,
                      cursor: 'pointer',
                      color: C.g400,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* Security Checklist */}
              <div style={{
                padding: '14px 16px',
                borderRadius: 12,
                background: '#F9FAFB',
                border: `1px solid ${C.g200}`,
                marginBottom: 24,
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.g600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 8 }}>
                  Password Requirements
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12.5,
                    color: isMinLength ? '#059669' : C.g500,
                    fontWeight: isMinLength ? 600 : 400,
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: isMinLength ? '#10B981' : C.g300,
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10
                    }}>
                      ✓
                    </div>
                    <span>Minimum of 6 characters in length</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12.5,
                    color: isMatch ? '#059669' : C.g500,
                    fontWeight: isMatch ? 600 : 400,
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: isMatch ? '#10B981' : C.g300,
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10
                    }}>
                      ✓
                    </div>
                    <span>Passwords match each other</span>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  style={{
                    padding: '11px 18px',
                    borderRadius: 10,
                    border: `1px solid ${C.g200}`,
                    background: '#fff',
                    color: C.g700,
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading || !isReady}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '11px 26px',
                    borderRadius: 10,
                    border: 'none',
                    background: isReady ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})` : C.g300,
                    color: '#fff',
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: isReady && !loading ? 'pointer' : 'not-allowed',
                    boxShadow: isReady ? '0 4px 14px rgba(79, 70, 229, 0.35)' : 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  <ShieldCheck size={16} />
                  {loading ? 'Updating Password...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default ChangePasswordPage;
